// frontend/src/pages/OffersPage.jsx
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";
import TornaAlMenu from "../components/TornaAlMenu";

const API_URL = process.env.REACT_APP_API_URL || "";

const mediaUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("blob:")) return url;
  return `${API_URL}${url.startsWith("/") ? url : `/${url}`}`;
};

const formatDateIT = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const money = (v) => {
  const n = Number(v || 0);
  if (!n) return "";
  return `${n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
};

export default function OffersPage() {
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const backTo = new URLSearchParams(search).get("from") === "app-management" ? "/gestione-app-clienti" : "/";
  const [rows, setRows] = useState([]);
  const [me, setMe] = useState(null);
  const [meLoading, setMeLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selectedOffer, setSelectedOffer] = useState(null);

  const tokenRole = (() => {
    try {
      const token = localStorage.getItem("access") || "";
      const payload = token.split(".")[1];
      if (!payload) return "";
      const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
      return String(json?.role || "").toUpperCase();
    } catch {
      return "";
    }
  })();

  const isCliente = pathname === "/app/offers" || String(me?.role || tokenRole || "").toUpperCase() === "CLIENTE";

  const loadMe = async () => {
    setMeLoading(true);

    if (!localStorage.getItem("access")) {
      setMe(null);
      setMeLoading(false);
      return;
    }

    try {
      const res = await authFetch(`${API_URL}/api/me/`);
      if (res.ok) {
        setMe(await res.json());
      }
    } catch {
      setMe(null);
    } finally {
      setMeLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const baseUrl = isCliente ? `${API_URL}/api/app/offers/` : `${API_URL}/api/offers/`;
      const url = q ? `${baseUrl}?q=${encodeURIComponent(q)}` : baseUrl;
      const res = localStorage.getItem("access")
        ? await authFetch(url)
        : await fetch(url);
      const json = await res.json();
      const list = Array.isArray(json) ? json : (json.results || []);
      setRows(list);
    } catch (e) {
      console.error(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

    const deleteOffer = async (offer) => {
      if (!offer?.id) return;
      const ok = window.confirm(`Vuoi eliminare definitivamente l'offerta "${offer.title}"?`);
      if (!ok) return;

      try {
        const res = await authFetch(`${API_URL}/api/offers/${offer.id}/`, {
          method: "DELETE",
        });

        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(txt || "Errore eliminazione offerta");
        }

        setRows((prev) => prev.filter((x) => Number(x.id) !== Number(offer.id)));
      } catch (e) {
        console.error(e);
        alert("Non sono riuscito a eliminare l'offerta.");
      }
    };

  useEffect(() => {
    loadMe();
    load();
    // eslint-disable-next-line
  }, []);

  const visibleRows = isCliente
    ? rows.filter((x) => x.active !== false)
    : rows;

  return (
    <div className="container mt-4">
      <TornaAlMenu to={backTo} />

      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h3 className="m-0">📣 Offerte & Promozioni</h3>

        <div className="d-flex gap-2">
          <input
            className="form-control"
            placeholder="Cerca offerta…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ minWidth: 220 }}
          />
          <button className="btn btn-outline-secondary" onClick={load}>Aggiorna</button>
          {!isCliente && <Link to="/offers/new" className="btn btn-primary">+ Nuova</Link>}
        </div>
      </div>

      {meLoading || loading ? (
        <p>Caricamento…</p>
      ) : visibleRows.length === 0 ? (
        <div className="alert alert-info rounded-4">
          {isCliente
            ? "Al momento non ci sono offerte attive. Torna presto: qui troverai promozioni acquistabili direttamente dall’app."
            : "Nessuna offerta."}
        </div>
      ) : isCliente ? (
          <div className="row g-3">
            {visibleRows.map((x) => (
              <div className="col-12 col-md-6 col-lg-4" key={x.id}>
                <div
                  className="card border-0 rounded-5 h-100 overflow-hidden"
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedOffer(x)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setSelectedOffer(x);
                  }}
                  style={{ background: "linear-gradient(180deg, #ffffff 0%, #fff8f1 100%)", boxShadow: "0 16px 38px rgba(91,15,27,0.13)", cursor: "pointer" }}
                >
                  <div
                    style={{
                      height: 210,
                      background: "linear-gradient(135deg, #5b0f1b 0%, #8b1e2d 55%, #211014 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    {x.image_url ? (
                      <img
                        src={mediaUrl(x.image_url)}
                        alt={x.title}
                        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", filter: "drop-shadow(0 14px 24px rgba(0,0,0,0.25))" }}
                      />
                    ) : (
                      <div style={{ fontSize: 64 }}>🍾</div>
                    )}
                  </div>

                  <div className="card-body p-4">
                    <div className="mb-2">
                      <span className="badge rounded-pill px-3 py-2" style={{ background: "#5b0f1b", color: "#fff", letterSpacing: 0.5 }}>
                        🔥 OFFERTA LIMITATA
                      </span>
                    </div>

                    <div className="small text-muted mb-1">{x.code || "OFFERTA"}</div>
                    <h3 className="mb-2 fw-bold" style={{ color: "#211014" }}>{x.title}</h3>

                    {x.description && (
                      <p
                        className="text-muted mb-3"
                        style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                      >
                        {x.description}
                      </p>
                    )}

                      {(x.old_price || x.price) && (
                        <div className="mb-3">
                          {x.old_price && (
                            <div className="text-muted" style={{ textDecoration: "line-through", fontSize: 18 }}>
                              {money(x.old_price)}
                            </div>
                          )}
                          {x.price && (
                            <div className="fw-bold" style={{ color: "#5b0f1b", fontSize: 38, lineHeight: 1 }}>
                              {money(x.price)}
                            </div>
                          )}
                        </div>
                      )}

                    <div className="small text-muted mb-3">
                      {formatDateIT(x.start_date)}
                      {x.end_date ? ` → ${formatDateIT(x.end_date)}` : ""}
                    </div>

                    <button
                      className="btn w-100 rounded-pill fw-bold py-3"
                      style={{ background: "#5b0f1b", color: "#fff", border: 0 }}
                      onClick={() => navigate(`/app/carrello?offer_id=${x.id}`)}
                    >
                      🛒 Aggiungi al carrello
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Codice</th>
                <th>Titolo</th>
                <th>Scope</th>
                <th>Periodo</th>
                <th>Attiva</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((x) => (
                <tr key={x.id}>
                  <td>{x.code}</td>
                  <td>{x.title}</td>
                  <td>{x.scope}</td>
                  <td>{x.start_date}{x.end_date ? ` → ${x.end_date}` : ""}</td>
                  <td>{x.active ? "✅" : "—"}</td>
                    <td>
                      <div className="d-flex gap-2 justify-content-end">
                        <Link to={`/offers/${x.id}`} className="btn btn-sm btn-outline-secondary">
                          Dettagli
                        </Link>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => deleteOffer(x)}
                        >
                          Elimina
                        </button>
                      </div>
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    {selectedOffer && (
      <div
        className="position-fixed top-0 start-0 w-100 h-100"
        style={{ background: "rgba(33,16,20,0.72)", zIndex: 9999, padding: 18, overflowY: "auto" }}
        onClick={() => setSelectedOffer(null)}
      >
        <div
          className="mx-auto my-3 rounded-5 overflow-hidden"
          style={{ maxWidth: 560, background: "#fff8f1", boxShadow: "0 24px 60px rgba(0,0,0,0.35)" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ background: "linear-gradient(135deg, #5b0f1b 0%, #211014 100%)", minHeight: 260, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {selectedOffer.image_url ? (
              <img src={mediaUrl(selectedOffer.image_url)} alt={selectedOffer.title} style={{ width: "100%", maxHeight: 360, objectFit: "contain" }} />
            ) : (
              <div style={{ fontSize: 72, padding: 40 }}>🍾</div>
            )}
          </div>

          <div className="p-4">
            <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
              <span className="badge rounded-pill px-3 py-2" style={{ background: "#5b0f1b", color: "#fff" }}>
                🔥 OFFERTA LIMITATA
              </span>
              <button className="btn btn-light rounded-circle" type="button" onClick={() => setSelectedOffer(null)}>
                ×
              </button>
            </div>

            <div className="small text-muted mb-1">{selectedOffer.code || "OFFERTA"}</div>
            <h2 className="fw-bold mb-3" style={{ color: "#211014" }}>{selectedOffer.title}</h2>

            {(selectedOffer.old_price || selectedOffer.price) && (
              <div className="mb-3">
                {selectedOffer.old_price && (
                  <div className="text-muted" style={{ textDecoration: "line-through", fontSize: 20 }}>
                    {money(selectedOffer.old_price)}
                  </div>
                )}
                {selectedOffer.price && (
                  <div className="fw-bold" style={{ color: "#5b0f1b", fontSize: 46, lineHeight: 1 }}>
                    {money(selectedOffer.price)}
                  </div>
                )}
              </div>
            )}

            {selectedOffer.description && (
              <p className="text-muted fs-5 mb-3">{selectedOffer.description}</p>
            )}

            <div className="small text-muted mb-4">
              Valida dal {formatDateIT(selectedOffer.start_date)}
              {selectedOffer.end_date ? ` al ${formatDateIT(selectedOffer.end_date)}` : ""}
            </div>

            <button
              className="btn w-100 rounded-pill fw-bold py-3"
              style={{ background: "#5b0f1b", color: "#fff", border: 0 }}
              onClick={() => navigate(`/app/carrello?offer_id=${selectedOffer.id}`)}
            >
              🛒 Aggiungi al carrello
            </button>
          </div>
        </div>
      </div>
    )}

    </div>
  );
}

